// src/components/BookCover/CoverRightSidebar.jsx
// ---------------------------------------------------------------------------
// Unified right sidebar for the Book Cover Designer.
// Five tabs:
//   Info        — version info + design criteria
//   Chat        — general comment thread between collaborators
//   Annotations — per-object comments; shows focused thread when an object is
//                 selected, or a grouped overview of all annotated objects
//   Team        — collaborator list + invite form
//   Activity    — changes / audit trail
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { API_BASE } from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import '../../styles/FeedbackPanel.css';
import '../../styles/CollaboratePanel.css';
import {
  TaskDetailModal, TaskCreateModal, TaskRow,
  Avatar, getCurrentUserId,
} from '../tasks/TaskModals';

// ── Helpers ─────────────────────────────────────────────────────────────────
const ROLE_COLORS = {
  author: '#D4AF37',
  design: '#8b5cf6',
  designer: '#8b5cf6',
  view_only: '#0ea5e9',
  feedback_reviewer: '#0ea5e9',
};

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
  return d.toLocaleDateString();
};

const getAuthHeaders = (json = true) => {
  const token = localStorage.getItem('rv_access_token');
  const h = {};
  if (json) h['Content-Type'] = 'application/json';
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

const ROLE_META = {
  author:    { label: 'Author',   icon: '✍️', color: '#D4AF37' },
  design:    { label: 'Designer', icon: '🎨', color: '#8b5cf6' },
  view_only: { label: 'Viewer',   icon: '👁️', color: '#0ea5e9' },
};

const ACTION_ICONS = {
  comment:       '💬',
  feedback:      '📝',
  design_save:   '💾',
  version_create:'🆕',
  invite:        '✉️',
  approve:       '✅',
  reject:        '❌',
  role_change:   '🔄',
  checkpoint:    '📌',
  restore:       '↩',
  unknown:       '•',
};

// ═══════════════════════════════════════════════════════════════════════════
//  INFO TAB
// ═══════════════════════════════════════════════════════════════════════════
function InfoTab({ draftId, versionId, isAuthor, versionData, onCriteriaUpdated }) {
  const initialCriteria = versionData.criteria || '';
  const [criteria, setCriteria] = useState(initialCriteria);
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState(initialCriteria);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const vc = versionData.criteria || '';
    setCriteria(vc);
    setInput(vc);
  }, [versionData.criteria]);

  const statusLabels = {
    in_progress: 'In Progress', drafting: 'Drafting',
    waiting_for_approval: 'Waiting for Approval', pending_approval: 'Pending Approval',
    approved: 'Author Approved', in_review: 'In Review', rejected: 'Rejected',
    reviewer_approved: 'Approved', published: 'Published', assigned: 'Assigned',
  };
  const statusLabel = statusLabels[versionData.coverStatus] || 'Draft';
  const statusClass = versionData.coverStatus
    ? `fp-status-badge fp-status-${versionData.coverStatus.replace(/_/g, '-')}`
    : 'fp-status-badge fp-status-draft';

  const handleSave = async () => {
    if (!draftId || !versionId) return;
    setSaving(true); setMsg('');
    try {
      const res = await fetch(
        `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/criteria`,
        { method: 'PUT', credentials: 'omit', headers: getAuthHeaders(), body: JSON.stringify({ criteria: input }) },
      );
      if (res.ok) {
        setCriteria(input); setEditing(false); setMsg('Saved!');
        onCriteriaUpdated?.(input);
        setTimeout(() => setMsg(''), 2000);
      } else {
        const err = await res.json().catch(() => ({}));
        setMsg(err.error || 'Failed to save');
      }
    } catch { setMsg('Network error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fp-scroll">
      {/* Version Info */}
      <div className="fp-section">
        <div className="fp-section-label">Version</div>
        <div className="fp-version-info">
          <span className="fp-version-name">{versionData.name || 'Unknown'}</span>
          <span className={statusClass}>{statusLabel}</span>
        </div>
      </div>

      {/* Design Criteria */}
      <div className="fp-section">
        <div className="fp-section-header">
          <span className="fp-section-label">Design Criteria</span>
          {isAuthor && !editing && (
            <button className="fp-edit-btn" onClick={() => setEditing(true)} title="Edit criteria">
              <EditIcon style={{ fontSize: 14 }} /> Edit
            </button>
          )}
        </div>

        {editing ? (
          <div className="fp-criteria-editor">
            <textarea
              className="fp-criteria-textarea"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Describe your vision for the cover..."
              rows={6} maxLength={2000} autoFocus
            />
            <div className="fp-criteria-meta"><span className="fp-char-count">{input.length} / 2000</span></div>
            <div className="fp-criteria-actions">
              <button className="fp-btn fp-btn-cancel" onClick={() => { setInput(criteria); setEditing(false); }} disabled={saving}>Cancel</button>
              <button className="fp-btn fp-btn-save" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
            {msg && <div className="fp-save-msg">{msg}</div>}
          </div>
        ) : criteria ? (
          <div className="fp-criteria-content">
            {criteria.split('\n').map((line, i) => <p key={i} className="fp-criteria-line">{line || '\u00A0'}</p>)}
          </div>
        ) : (
          <div className="fp-empty">
            {isAuthor ? 'No criteria set. Click "Edit" to describe your vision.' : 'No design criteria provided by the author yet.'}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  CHAT TAB  (general comments / feedback between collaborators)
// ═══════════════════════════════════════════════════════════════════════════
function ChatTab({ draftId, versionId, isAuthor, collaboratorRole }) {
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUserId = currentUser.id || currentUser._id;
  const feedbackMode = collaboratorRole === 'view_only' && !isAuthor;

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    if (!draftId || !versionId) return;
    setLoading(true);
    try {
      const ep = feedbackMode
        ? `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/feedback`
        : `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/comments`;
      const res = await fetch(ep, { credentials: 'omit', headers: getAuthHeaders(false) });
      if (res.ok) {
        const data = await res.json();
        setComments(feedbackMode ? (data.feedback || []) : (data.comments || []));
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [draftId, versionId, feedbackMode]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [comments.length]);

  const post = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const ep = feedbackMode
        ? `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/feedback`
        : `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/comments`;
      const res = await fetch(ep, {
        method: 'POST', credentials: 'omit', headers: getAuthHeaders(),
        body: JSON.stringify({ text: input.trim() }),
      });
      if (res.ok) { setInput(''); await load(); }
    } catch { /* ignore */ }
    finally { setSending(false); }
  };

  const del = async (cid) => {
    try {
      const ep = feedbackMode
        ? `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/feedback/${cid}`
        : `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/comments/${cid}`;
      const res = await fetch(ep, { method: 'DELETE', credentials: 'omit', headers: getAuthHeaders(false) });
      if (res.ok) setComments(prev => prev.filter(c => c.id !== cid));
    } catch { /* ignore */ }
  };

  const canPost = isAuthor || collaboratorRole === 'design' || collaboratorRole === 'view_only';

  return (
    <>
      <div className="fp-scroll">
        <div className="fp-section fp-comments-section">
          {loading && <div className="fp-empty">Loading...</div>}
          {!loading && comments.length === 0 && (
            <div className="fp-empty">
              {feedbackMode ? 'No feedback yet. Share your thoughts!' : 'No comments yet. Start the conversation!'}
            </div>
          )}
          <div className="fp-comments-list">
            {comments.map(c => (
              <div key={c.id} className={`fp-comment ${c.role === 'author' ? 'fp-comment-author' : c.role === 'feedback_reviewer' || c.role === 'view_only' ? 'fp-comment-reviewer' : 'fp-comment-designer'}`}>
                <div className="fp-comment-header">
                  <span className="fp-comment-name">{c.userName}</span>
                  <span className="fp-comment-role">{c.role}</span>
                  {c.userId === currentUserId && (
                    <button className="fp-comment-delete" onClick={() => del(c.id)} title="Delete">
                      <DeleteIcon style={{ fontSize: 13 }} />
                    </button>
                  )}
                </div>
                <div className="fp-comment-text">{c.text}</div>
                <div className="fp-comment-time">
                  {c.createdAt ? new Date(c.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
        </div>
      </div>

      {canPost && (
        <div className="fp-composer">
          <textarea
            className="fp-composer-input"
            value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); post(); } }}
            placeholder={feedbackMode ? 'Share your feedback...' : 'Write a comment...'}
            rows={2} maxLength={2000}
          />
          <button className="fp-btn fp-btn-send" onClick={post} disabled={sending || !input.trim()}>
            {sending ? '...' : 'Send'}
          </button>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  ANNOTATIONS TAB
//  When an object is focused: shows thread for that object (ObjectCommentPanel)
//  When nothing focused: shows all annotated objects with their threads
// ═══════════════════════════════════════════════════════════════════════════
function AnnotationsTab({
  draftId, versionId, canvas, canvasReady, faceDims, triggerChange,
  activeObjComment, onCloseObjComment,
  objCommentCounts, onObjCountChange,
  onHighlightObject,
}) {
  if (activeObjComment) {
    return (
      <SingleObjectThread
        draftId={draftId} versionId={versionId}
        collabId={activeObjComment.collabId}
        objectLabel={activeObjComment.label}
        canvas={canvas}
        faceDims={faceDims}
        triggerChange={triggerChange}
        onClose={onCloseObjComment}
        onCountChange={onObjCountChange}
      />
    );
  }
  return (
    <AllAnnotationsView
      draftId={draftId} versionId={versionId} canvas={canvas}
      canvasReady={canvasReady}
      faceDims={faceDims}
      objCommentCounts={objCommentCounts}
      onHighlightObject={onHighlightObject}
      onObjCountChange={onObjCountChange}
      triggerChange={triggerChange}
    />
  );
}

// ── Single object thread (replaces ObjectCommentPanel) ──────────────────
function SingleObjectThread({ draftId, versionId, collabId, objectLabel, canvas, faceDims, triggerChange, onClose, onCountChange }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState('');
  const listEndRef = useRef(null);

  // Compute the display label fresh from the canvas object each render
  const fabricObj = canvas ? findByCollabId(canvas.getObjects(), collabId) : null;
  const allAnnotated = canvas ? canvas.getObjects().filter(o => o._collabId) : [];
  const displayLabel = fabricObj ? buildLabel(fabricObj, faceDims, allAnnotated) : objectLabel;
  const isBookAnnotation = collabId === '__book__';

  const startRename = () => {
    setLabelDraft(fabricObj?._label || displayLabel);
    setEditingLabel(true);
  };
  const commitLabel = () => {
    const trimmed = labelDraft.trim();
    if (trimmed && fabricObj) { fabricObj._label = trimmed; triggerChange?.(); }
    setEditingLabel(false);
  };

  const fetchComments = useCallback(async () => {
    if (!draftId || !versionId || !collabId) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/object-comments?collabId=${collabId}`,
        { credentials: 'omit', headers: getAuthHeaders(false) },
      );
      if (res.ok) { const data = await res.json(); setComments(data.comments || []); }
    } catch (e) { console.error('fetch failed', e); }
    finally { setLoading(false); }
  }, [draftId, versionId, collabId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);
  useEffect(() => { const iv = setInterval(fetchComments, 10000); return () => clearInterval(iv); }, [fetchComments]);
  useEffect(() => { listEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [comments.length]);

  const handlePost = async () => {
    if (!newText.trim() || posting) return;
    setPosting(true); setError('');
    try {
      const res = await fetch(
        `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/object-comments`,
        { method: 'POST', credentials: 'omit', headers: getAuthHeaders(), body: JSON.stringify({ collabId, text: newText.trim() }) },
      );
      if (res.ok) { setNewText(''); await fetchComments(); onCountChange?.(collabId, comments.length + 1); }
      else { const d = await res.json().catch(() => ({})); setError(d.error || 'Failed to post'); }
    } catch { setError('Network error'); }
    finally { setPosting(false); }
  };

  const handleDelete = async (commentId) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/object-comments/${commentId}`,
        { method: 'DELETE', credentials: 'omit', headers: getAuthHeaders(false) },
      );
      if (res.ok) { await fetchComments(); onCountChange?.(collabId, Math.max(0, comments.length - 1)); }
    } catch (e) { console.error('delete failed', e); }
  };

  const handleResolve = async (commentId) => {
    try {
      await fetch(
        `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/object-comments/${commentId}/resolve`,
        { method: 'POST', credentials: 'omit', headers: getAuthHeaders(false) },
      );
      await fetchComments();
    } catch (e) { console.error('resolve failed', e); }
  };

  const handleFocus = () => {
    if (!canvas || !collabId) return;
    const obj = findByCollabId(canvas.getObjects(), collabId);
    if (!obj) return;
    canvas.setActiveObject(obj);
    canvas.requestRenderAll();
  };

  return (
    <>
      {/* Sub-header showing which element is focused */}
      <div className="crs-annotation-header">
        <button className="crs-back-btn" onClick={onClose} title="Back to all annotations">
          <ChevronLeftIcon style={{ fontSize: 16 }} />
        </button>
        {editingLabel ? (
          <input
            className="crs-annotation-label-input"
            autoFocus
            value={labelDraft}
            onChange={e => setLabelDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') commitLabel(); if (e.key === 'Escape') setEditingLabel(false); }}
            onBlur={commitLabel}
            maxLength={60}
          />
        ) : (
          <span className="crs-annotation-label" onDoubleClick={!isBookAnnotation ? startRename : undefined}>{isBookAnnotation ? '📖' : '💬'} {displayLabel}</span>
        )}
        {!isBookAnnotation && <button className="crs-rename-btn" onClick={startRename} title="Rename this element">✏️</button>}
        {!isBookAnnotation && fabricObj && (
          <button className="crs-focus-btn" onClick={handleFocus} title="Focus on canvas">
            <CenterFocusStrongIcon style={{ fontSize: 16 }} />
          </button>
        )}
      </div>

      {/* Comment list */}
      <div className="collab-side-body" style={{ flex: 1, overflowY: 'auto' }}>
        {loading && <p style={{ color: '#aaa', fontSize: '0.8rem', textAlign: 'center' }}>Loading…</p>}
        {!loading && comments.length === 0 && (
          <p style={{ color: '#aaa', fontSize: '0.8rem', textAlign: 'center', padding: 20 }}>
            No comments yet on this element.
          </p>
        )}
        {comments.map(c => (
          <div key={c._id || c.id} className="collab-comment-item" style={c.resolved ? { opacity: 0.55 } : undefined}>
            <div className="collab-comment-header">
              <span className="collab-comment-author" style={{ color: ROLE_COLORS[c.role] || '#ccc' }}>{c.userName || 'User'}</span>
              <span className="collab-comment-time">{fmtTime(c.createdAt)}</span>
            </div>
            <p className="collab-comment-text">{c.text}</p>
            <div className="collab-comment-actions">
              <button className="collab-comment-action-btn" title={c.resolved ? 'Unresolve' : 'Resolve'} onClick={() => handleResolve(c._id)}>
                {c.resolved ? <CheckCircleIcon style={{ fontSize: 15, color: '#4caf50' }} /> : <CheckCircleOutlineIcon style={{ fontSize: 15 }} />}
              </button>
              <button className="collab-comment-action-btn" title="Delete" onClick={() => handleDelete(c._id)}>
                <DeleteIcon style={{ fontSize: 15 }} />
              </button>
            </div>
            {c.resolved && <span style={{ fontSize: '0.65rem', color: '#4caf50' }}>✓ Resolved{c.resolvedBy ? ` by ${c.resolvedBy}` : ''}</span>}
          </div>
        ))}
        <div ref={listEndRef} />
      </div>

      {/* Post form */}
      <div className="collab-side-footer" style={{ padding: '8px 10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        {error && <p style={{ color: '#ef4444', fontSize: '0.75rem', margin: '0 0 4px' }}>{error}</p>}
        <div style={{ display: 'flex', gap: 6 }}>
          <input type="text" className="collab-comment-input" placeholder="Add a comment…"
            value={newText} onChange={e => setNewText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handlePost()} disabled={posting} style={{ flex: 1 }}
          />
          <button className="collab-send-comment-btn" onClick={handlePost} disabled={posting || !newText.trim()} title="Send">
            <SendIcon style={{ fontSize: 14 }} />
          </button>
        </div>
      </div>
    </>
  );
}

// Find an object by _collabId, searching recursively inside groups.
// (canvas.getObjects() only returns top-level objects; an annotated object
// that was later grouped lives nested inside a group.)
function findByCollabId(objects, cid) {
  if (!objects) return null;
  for (const o of objects) {
    if (o._collabId === cid) return o;
    if (typeof o.getObjects === 'function') {
      const inner = findByCollabId(o.getObjects(), cid);
      if (inner) return inner;
    }
  }
  return null;
}

// ── Label helpers ────────────────────────────────────────────────────────────

/** Raw auto-label: object name + fill color hint + canvas face hint. */
function getAutoLabel(obj, faceDims) {
  const base = obj.name || obj.text?.slice(0, 24) || obj.type || 'Element';
  let colorHint = '';
  const fill = obj.fill;
  if (fill && typeof fill === 'string' && fill !== 'transparent' && fill !== 'rgba(0,0,0,0)') {
    if (fill.length <= 9) colorHint = fill; // plain hex/short color only — skip gradients
  }
  let faceHint = '';
  if (faceDims && obj.left != null) {
    const { faceW, spineW } = faceDims;
    const cx = (obj.left || 0) + (obj.width || 0) / 2;
    if (cx < faceW) faceHint = 'back';
    else if (cx < faceW + spineW) faceHint = 'spine';
    else faceHint = 'front';
  }
  return [base, colorHint, faceHint].filter(Boolean).join(' · ');
}

/**
 * Build the display label, appending a counter suffix when multiple
 * objects share the same auto-label (e.g. "shape-rectangle · #e74c3c · front (2)").
 * @param {object}   obj      – Fabric canvas object
 * @param {object}   faceDims – { faceW, spineW } or null
 * @param {object[]} peers    – all annotated canvas objects (for dup detection)
 */
function buildLabel(obj, faceDims, peers) {
  if (obj._label) return obj._label;
  const auto = getAutoLabel(obj, faceDims);
  if (!peers || peers.length <= 1) return auto;
  const sameAuto = peers.filter(o => !o._label && getAutoLabel(o, faceDims) === auto);
  if (sameAuto.length <= 1) return auto;
  const idx = sameAuto.indexOf(obj);
  return idx <= 0 ? auto : `${auto} (${idx + 1})`;
}

// ── All annotations overview ────────────────────────────────────────────
function AllAnnotationsView({ draftId, versionId, canvas, canvasReady, faceDims, objCommentCounts, onHighlightObject, onObjCountChange, triggerChange }) {
  const [allComments, setAllComments] = useState({}); // { collabId: { label, comments: [] } }
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [groupLabelDraft, setGroupLabelDraft] = useState('');
  const toast = useToast();
  const BOOK_COLLAB_ID = '__book__';
  const bookCount = objCommentCounts[BOOK_COLLAB_ID] || 0;

  const startGroupRename = (cid, currentLabel) => {
    const obj = canvas ? findByCollabId(canvas.getObjects(), cid) : null;
    setGroupLabelDraft(obj?._label || currentLabel);
    setEditingGroupId(cid);
  };

  const commitGroupLabel = (cid) => {
    const trimmed = groupLabelDraft.trim();
    if (trimmed) {
      const obj = canvas ? findByCollabId(canvas.getObjects(), cid) : null;
      if (obj) { obj._label = trimmed; triggerChange?.(); }
      setAllComments(prev => prev[cid] ? { ...prev, [cid]: { ...prev[cid], label: trimmed } } : prev);
    }
    setEditingGroupId(null);
  };

  useEffect(() => {
    if (!draftId || !versionId || Object.keys(objCommentCounts).length === 0) { setLoading(false); return; }

    const fetchAll = async () => {
      setLoading(true);
      const groups = {};
      const allAnnotated = canvas ? canvas.getObjects().filter(o => o._collabId) : [];

      for (const [cid, count] of Object.entries(objCommentCounts)) {
        if (count === 0) continue;

        const obj = canvas ? findByCollabId(canvas.getObjects(), cid) : null;

        const label = obj
          ? buildLabel(obj, faceDims, allAnnotated)
          : (allComments[cid]?.label || 'Element');

        try {
          const res = await fetch(
            `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/object-comments?collabId=${cid}`,
            { credentials: 'omit', headers: getAuthHeaders(false) },
          );
          if (res.ok) {
            const data = await res.json();
            groups[cid] = { label, comments: data.comments || [] };
          }
        } catch { /* skip */ }
      }

      setAllComments(groups);
      setLoading(false);
    };

    fetchAll();
  }, [canvas, canvasReady, faceDims, draftId, versionId, objCommentCounts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Instantly remove entries from the panel when objCommentCounts loses a key
  // (e.g. when the canvas object is deleted) — no re-fetch needed.
  useEffect(() => {
    setAllComments(prev => {
      const removed = Object.keys(prev).filter(k => !objCommentCounts[k]);
      if (removed.length === 0) return prev;
      const next = { ...prev };
      removed.forEach(k => delete next[k]);
      return next;
    });
  }, [objCommentCounts]);

  const groupIds = Object.keys(allComments).filter(k => k !== BOOK_COLLAB_ID);

  const handleHoverGroup = (collabId) => {
    if (!canvas) return;
    const obj = findByCollabId(canvas.getObjects(), collabId);
    if (obj) {
      canvas.setActiveObject(obj);
      canvas.requestRenderAll();
    }
  };

  const handleLeaveGroup = () => {
    if (!canvas) return;
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  };

  const handleClickGroup = (collabId) => {
    // Use the label we already fetched — don't require the canvas object to exist
    const label = allComments[collabId]?.label || 'Element';
    onHighlightObject?.({ collabId, label });
  };

  const handleDeleteGroup = (e, collabId) => {
    e.stopPropagation();
    setConfirmDeleteId(collabId);
  };

  const confirmDelete = async () => {
    const collabId = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      const token = localStorage.getItem('rv_access_token');
      const res = await fetch(
        `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/object-comments?collabId=${collabId}`,
        { method: 'DELETE', credentials: 'omit', headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) {
        const msg = await res.text().catch(() => '');
        toast.error(`Failed to delete comments (${res.status})${msg ? ': ' + msg : ''}`);
        return;
      }
      setAllComments(prev => { const n = { ...prev }; delete n[collabId]; return n; });
      onObjCountChange?.(collabId, 0);
      toast.success('Comments deleted');
    } catch (err) {
      toast.error('Failed to delete comments: ' + err.message);
    }
  };

  return (
    <div className="fp-scroll">
      {/* ── Entire Cover permanent entry ── */}
      <div className="crs-annotation-group">
        <div
          className="crs-annotation-group-header"
          onClick={() => onHighlightObject?.({ collabId: BOOK_COLLAB_ID, label: 'Entire Cover' })}
          style={{ cursor: 'pointer' }}
        >
          <span className="crs-annotation-group-icon">📖</span>
          <span className="crs-annotation-group-label">Entire Cover</span>
          {bookCount > 0 && <span className="crs-annotation-group-count">{bookCount}</span>}
          <button
            className="crs-focus-btn"
            onClick={(e) => { e.stopPropagation(); onHighlightObject?.({ collabId: BOOK_COLLAB_ID, label: 'Entire Cover' }); }}
            title="Open thread"
          >
            <ChevronRightIcon style={{ fontSize: 16 }} />
          </button>
        </div>
      </div>

      {loading && <div className="fp-empty" style={{ padding: 20, textAlign: 'center' }}>Loading annotations...</div>}

      {!loading && groupIds.length === 0 && (
        <div className="fp-empty" style={{ padding: 20, textAlign: 'center' }}>
          No element annotations yet.
          <br />
          <span style={{ fontSize: '0.75rem' }}>Right-click any canvas element and choose "Add comment".</span>
        </div>
      )}

      {groupIds.map(cid => {
        const group = allComments[cid];
        const isExpanded = expandedId === cid;
        return (
          <div
            key={cid}
            className="crs-annotation-group"
            onMouseEnter={() => handleHoverGroup(cid)}
            onMouseLeave={handleLeaveGroup}
          >
            <div
              className="crs-annotation-group-header"
              onClick={() => setExpandedId(isExpanded ? null : cid)}
            >
              <span className="crs-annotation-group-icon">💬</span>
              {editingGroupId === cid ? (
                <input
                  className="crs-annotation-label-input"
                  autoFocus
                  value={groupLabelDraft}
                  onChange={e => setGroupLabelDraft(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitGroupLabel(cid);
                    if (e.key === 'Escape') setEditingGroupId(null);
                  }}
                  onBlur={() => commitGroupLabel(cid)}
                  onClick={e => e.stopPropagation()}
                  maxLength={60}
                />
              ) : (
                <span className="crs-annotation-group-label" onDoubleClick={e => { e.stopPropagation(); startGroupRename(cid, group.label); }}>{group.label}</span>
              )}
              <button
                className="crs-rename-btn"
                onClick={e => { e.stopPropagation(); startGroupRename(cid, group.label); }}
                title="Rename"
              >✏️</button>
              <span className="crs-annotation-group-count">{group.comments.length}</span>
              <button
                className="crs-annotation-delete-btn"
                onClick={(e) => handleDeleteGroup(e, cid)}
                title="Delete all comments for this element"
              >
                <DeleteOutlineIcon style={{ fontSize: 14 }} />
              </button>
              <button
                className="crs-focus-btn"
                onClick={(e) => { e.stopPropagation(); handleClickGroup(cid); }}
                title="Open thread"
              >
                <ChevronRightIcon style={{ fontSize: 16 }} />
              </button>
            </div>

            {isExpanded && (
              <div className="crs-annotation-group-body">
                {group.comments.slice(0, 3).map(c => (
                  <div key={c._id || c.id} className="crs-annotation-preview">
                    <span className="crs-annotation-preview-author" style={{ color: ROLE_COLORS[c.role] || '#ccc' }}>
                      {c.userName || 'User'}
                    </span>
                    <span className="crs-annotation-preview-text">{c.text}</span>
                    <span className="crs-annotation-preview-time">{fmtTime(c.createdAt)}</span>
                  </div>
                ))}
                {group.comments.length > 3 && (
                  <button className="crs-annotation-more" onClick={() => handleClickGroup(cid)}>
                    View all {group.comments.length} comments →
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Delete confirmation modal */}
      {confirmDeleteId && (
        <div className="checkpoint-restore-overlay" onClick={() => setConfirmDeleteId(null)}>
          <div className="checkpoint-restore-modal" onClick={e => e.stopPropagation()}>
            <h3>Delete comments?</h3>
            <p className="checkpoint-restore-modal-label">
              "{allComments[confirmDeleteId]?.label || 'Element'}"
            </p>
            <p className="checkpoint-restore-modal-info">
              All comments for this element will be permanently deleted and cannot be recovered.
            </p>
            <div className="checkpoint-restore-modal-actions">
              <button className="checkpoint-restore-modal-cancel" onClick={() => setConfirmDeleteId(null)}>
                Cancel
              </button>
              <button className="annot-delete-confirm-btn" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  TEAM TAB  (collaborators list + invite form)
// ═══════════════════════════════════════════════════════════════════════════
function TeamTab({ draftId, versionId, isAuthor, onInvite }) {
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profilePics, setProfilePics] = useState({});
  const [changingRole, setChangingRole] = useState(null);

  // Fetch collaborators
  useEffect(() => {
    if (!draftId || !versionId) return;
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/collaborators`,
          { credentials: 'omit', headers: getAuthHeaders(false) },
        );
        if (res.ok && alive) {
          const data = await res.json();
          setCollaborators(data.collaborators || data || []);
        }
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    };
    load();
    return () => { alive = false; };
  }, [draftId, versionId]);

  // Fetch profile pictures
  useEffect(() => {
    if (!collaborators.length) return;
    const fetchPics = async () => {
      const pics = {};
      for (const c of collaborators) {
        const uid = c.userId;
        if (!uid || pics[uid]) continue;
        try {
          const res = await fetch(`${API_BASE}/api/profile/user/${uid}/picture-url`, {
            headers: getAuthHeaders(false),
          });
          if (res.ok) {
            const data = await res.json();
            if (data.url) pics[uid] = data.url;
          }
        } catch { /* ignore */ }
      }
      setProfilePics(pics);
    };
    fetchPics();
  }, [collaborators]);

  // Change role
  const handleRoleChange = async (userId, newRole) => {
    setChangingRole(userId);
    try {
      const res = await fetch(
        `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/collaborators/${userId}`,
        {
          method: 'PUT', credentials: 'omit', headers: getAuthHeaders(),
          body: JSON.stringify({ role: newRole }),
        },
      );
      if (res.ok) {
        setCollaborators(prev =>
          prev.map(c => (c.userId === userId ? { ...c, role: newRole } : c)),
        );
      }
    } catch { /* ignore */ }
    finally { setChangingRole(null); }
  };

  // Remove collaborator
  const handleRemove = async (userId) => {
    if (!window.confirm('Remove this collaborator?')) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/collaborators/${userId}`,
        { method: 'DELETE', credentials: 'omit', headers: getAuthHeaders(false) },
      );
      if (res.ok) {
        setCollaborators(prev => prev.filter(c => c.userId !== userId));
      }
    } catch { /* ignore */ }
  };

  return (
    <div className="fp-scroll">
      {/* Invite button */}
      {isAuthor && (
        <div className="fp-section">
          <button
            className="crs-invite-toggle"
            onClick={() => onInvite?.()}
          >
            <PersonAddIcon style={{ fontSize: 15 }} />
            <span>Invite Collaborator</span>
          </button>
        </div>
      )}

      {/* Collaborator list */}
      <div className="fp-section">
        <div className="fp-section-label">Collaborators</div>

        {loading && <div className="fp-empty">Loading collaborators...</div>}

        {!loading && collaborators.length === 0 && (
          <div className="fp-empty">
            No collaborators on this version yet.
            {isAuthor && <span> Click "Invite Collaborator" above to add someone.</span>}
          </div>
        )}

        {collaborators.map(c => {
          const meta = ROLE_META[c.role] || ROLE_META.view_only;
          return (
            <div key={c.userId} className="collab-user-card">
              <div className="collab-user-avatar">
                {profilePics[c.userId] ? (
                  <img src={profilePics[c.userId]} alt="" className="collab-avatar-img" />
                ) : (
                  <PersonIcon style={{ fontSize: 22, color: '#aaa' }} />
                )}
              </div>
              <div className="collab-user-info">
                <span className="collab-user-name">{c.userName || c.username || 'User'}</span>
                <span className="collab-user-role" style={{ color: meta.color }}>
                  {meta.icon} {meta.label}
                </span>
              </div>
              {isAuthor && c.role !== 'author' && (
                <div className="collab-user-actions">
                  <select
                    className="collab-role-select"
                    value={c.role}
                    disabled={changingRole === c.userId}
                    onChange={e => handleRoleChange(c.userId, e.target.value)}
                    title="Change role"
                  >
                    <option value="design">Designer</option>
                    <option value="view_only">Viewer</option>
                  </select>
                  <button
                    className="collab-remove-btn"
                    onClick={() => handleRemove(c.userId)}
                    title="Remove collaborator"
                  >
                    <DeleteOutlineIcon style={{ fontSize: 16 }} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  ACTIVITY TAB  (changes / audit trail + checkpoint log)
// ═══════════════════════════════════════════════════════════════════════════
function ActivityTab({ draftId, versionId, onRestoreCheckpoint, checkpointRestoring }) {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'checkpoints' | 'user'

  const reload = useCallback(() => {
    if (!draftId || !versionId) return;
    setLoading(true);
    fetch(
      `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/activity`,
      { credentials: 'omit', headers: getAuthHeaders(false) },
    )
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) setChanges(data.activities || data.activity || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [draftId, versionId]);

  useEffect(() => { reload(); }, [reload]);

  // Derived lists
  const checkpointEntries = changes.filter(c => c.action === 'checkpoint' || c.action === 'restore');
  const displayed = filter === 'checkpoints' ? checkpointEntries : changes;

  // Group changes by user
  const changesByUser = {};
  displayed.forEach(c => {
    const key = c.userName || c.userId || 'Unknown';
    if (!changesByUser[key]) changesByUser[key] = [];
    changesByUser[key].push(c);
  });

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
    if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const CHECKPOINT_ICONS = {
    checkpoint: '📌',
    restore: '↩',
  };

  return (
    <div className="fp-scroll">
      {/* Filter toggle */}
      <div className="collab-changes-toggle">
        <button
          className={`collab-toggle-btn${filter === 'all' ? ' active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All Activity
        </button>
        <button
          className={`collab-toggle-btn${filter === 'checkpoints' ? ' active' : ''}`}
          onClick={() => setFilter('checkpoints')}
        >
          Checkpoints{checkpointEntries.length > 0 ? ` (${checkpointEntries.length})` : ''}
        </button>
        <button
          className={`collab-toggle-btn${filter === 'user' ? ' active' : ''}`}
          onClick={() => setFilter('user')}
        >
          By User
        </button>
      </div>

      {loading && <div className="fp-empty">Loading activity...</div>}

      {!loading && displayed.length === 0 && (
        <div className="fp-empty" style={{ padding: 20, textAlign: 'center' }}>
          <InfoOutlinedIcon style={{ fontSize: 18, marginBottom: 4 }} />
          <br />
          {filter === 'checkpoints' ? 'No checkpoints saved yet.' : 'No recorded activity yet.'}
          <br />
          <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>
            {filter === 'checkpoints'
              ? 'Save a checkpoint from the PROGRESS tab to start tracking history.'
              : 'Activity will appear here as collaborators interact.'}
          </span>
        </div>
      )}

      {/* Timeline view (all or checkpoints only) */}
      {!loading && filter !== 'user' && displayed.map(c => {
        const isCheckpoint = c.action === 'checkpoint' || c.action === 'restore';
        return (
          <div key={c.id || c.createdAt} className={`collab-change-card${isCheckpoint ? ' collab-change-card--checkpoint' : ''}`}>
            <span className="collab-change-icon">
              {CHECKPOINT_ICONS[c.action] || ACTION_ICONS[c.action] || ACTION_ICONS.unknown}
            </span>
            <div className="collab-change-body">
              <span className="collab-change-user">
                {c.userName || 'Unknown'}
                {c.userRole && <span className={`collab-change-role-badge collab-role--${c.userRole}`}>{c.userRole}</span>}
              </span>
              <span className="collab-change-desc">{c.description}</span>
              <span className="collab-change-time">{formatTime(c.createdAt)}</span>
              {/* Restore action for checkpoint entries */}
              {c.action === 'checkpoint' && c.checkpointId && onRestoreCheckpoint && (
                <button
                  className="collab-change-restore-btn"
                  disabled={checkpointRestoring}
                  onClick={() => { onRestoreCheckpoint(c.checkpointId); setTimeout(reload, 1500); }}
                >
                  {checkpointRestoring ? '⏳' : '↩'} Restore
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* Grouped-by-user view */}
      {!loading && filter === 'user' && Object.entries(changesByUser).map(([userName, items]) => (
        <div key={userName} className="collab-user-group">
          <div className="collab-user-group-header">
            <PersonIcon style={{ fontSize: 16 }} />
            <span>{userName}</span>
            <span className="collab-user-group-count">{items.length}</span>
          </div>
          {items.map(c => {
            const isCheckpoint = c.action === 'checkpoint' || c.action === 'restore';
            return (
              <div key={c.id || c.createdAt} className={`collab-change-card compact${isCheckpoint ? ' collab-change-card--checkpoint' : ''}`}>
                <span className="collab-change-icon">
                  {CHECKPOINT_ICONS[c.action] || ACTION_ICONS[c.action] || ACTION_ICONS.unknown}
                </span>
                <div className="collab-change-body">
                  <span className="collab-change-desc">{c.description}</span>
                  <span className="collab-change-time">{formatTime(c.createdAt)}</span>
                  {c.action === 'checkpoint' && c.checkpointId && onRestoreCheckpoint && (
                    <button
                      className="collab-change-restore-btn"
                      disabled={checkpointRestoring}
                      onClick={() => { onRestoreCheckpoint(c.checkpointId); setTimeout(reload, 1500); }}
                    >
                      {checkpointRestoring ? '⏳' : '↩'} Restore
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  TASKS TAB — flat task manager for cover versions
// ═══════════════════════════════════════════════════════════════════════════

function TasksTab({ draftId, versionId, isAuthor, draftTitle }) {
  const toast = useToast();
  const navigate = useNavigate();
  const currentUserId = useMemo(() => getCurrentUserId(), []);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);
  const [avatars, setAvatars] = useState({});
  const [openTaskId, setOpenTaskId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [canManuscript, setCanManuscript] = useState(false);
  const [hasManuscript, setHasManuscript] = useState(false);

  const token = localStorage.getItem('rv_access_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  const base = `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/tasks`;

  // ── Fetch tasks (also returns assignees list) ──
  const fetchTasks = useCallback(async () => {
    try {
      const res = await fetch(base, { credentials: 'omit', headers });
      if (res.ok) {
        const d = await res.json();
        setTasks(d.tasks || []);
        if (d.assignees) setTeam(d.assignees);
        setCanManuscript(!!d.can_manuscript);
        setHasManuscript(!!d.has_manuscript);
      }
    } catch (e) { console.error('[TasksTab] fetch error', e); }
    finally { setLoading(false); }
  }, [base]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (draftId && versionId) fetchTasks(); }, [draftId, versionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch avatars when team changes ──
  useEffect(() => {
    if (!team.length) return;
    let alive = true;
    (async () => {
      const pics = {};
      for (const u of team) {
        try {
          const res = await fetch(`${API_BASE}/api/profile/user/${u.id}/picture-url`, {
            headers: getAuthHeaders(false),
          });
          if (res.ok) {
            const d = await res.json();
            if (d.url) pics[u.id] = d.url;
          }
        } catch { /* ignore */ }
      }
      if (alive) setAvatars(pics);
    })();
    return () => { alive = false; };
  }, [team]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Start task (assignee: unstarted → in_progress) ──
  const startTask = async (taskId) => {
    try {
      const res = await fetch(`${base}/${taskId}/start`, {
        method: 'PUT', credentials: 'omit', headers,
      });
      if (res.ok) { const d = await res.json(); setTasks(d.tasks || []); return true; }
      const e = await res.json().catch(() => ({}));
      toast.error(e.error || 'Could not start task');
    } catch (e) { console.error('[TasksTab] start error', e); }
    return false;
  };

  // ── Mark complete (per-assignee) ──
  const markComplete = async (taskId) => {
    try {
      const res = await fetch(`${base}/${taskId}/complete`, {
        method: 'PUT', credentials: 'omit', headers,
        body: JSON.stringify({ completed: true }),
      });
      if (res.ok) { const d = await res.json(); setTasks(d.tasks || []); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.error || 'Could not mark complete'); }
    } catch (e) { console.error('[TasksTab] complete error', e); }
  };

  // ── Cancel task (author only) ──
  const cancelTask = async (taskId) => {
    try {
      const res = await fetch(`${base}/${taskId}/cancel`, {
        method: 'PUT', credentials: 'omit', headers,
      });
      if (res.ok) { const d = await res.json(); setTasks(d.tasks || []); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.error || 'Could not cancel task'); }
    } catch (e) { console.error('[TasksTab] cancel error', e); }
  };

  // ── Unassign self (collaborator only) ──
  const unassignSelf = async (taskId) => {
    try {
      const res = await fetch(`${base}/${taskId}/unassign`, {
        method: 'PUT', credentials: 'omit', headers,
      });
      if (res.ok) { const d = await res.json(); setTasks(d.tasks || []); setOpenTaskId(null); return true; }
      const e = await res.json().catch(() => ({}));
      toast.error(e.error || 'Could not unassign');
    } catch (e) { console.error('[TasksTab] unassign error', e); }
    return false;
  };

  // ── Update single task (author only) ──
  const updateTask = async (taskId, patch) => {
    try {
      const res = await fetch(`${base}/${taskId}`, {
        method: 'PUT', credentials: 'omit', headers,
        body: JSON.stringify(patch),
      });
      if (res.ok) { const d = await res.json(); setTasks(d.tasks || []); return true; }
      const e = await res.json().catch(() => ({}));
      toast.error(e.error || 'Could not update task');
    } catch (e) { console.error('[TasksTab] update error', e); }
    return false;
  };

  // ── Add comment ──
  const addComment = async (taskId, text) => {
    try {
      const res = await fetch(`${base}/${taskId}/comments`, {
        method: 'POST', credentials: 'omit', headers,
        body: JSON.stringify({ text }),
      });
      if (res.ok) { const d = await res.json(); setTasks(d.tasks || []); return true; }
      const e = await res.json().catch(() => ({}));
      toast.error(e.error || 'Could not add comment');
    } catch (e) { console.error('[TasksTab] comment error', e); }
    return false;
  };

  // ── Go to manuscript ──
  const goToManuscript = () => {
    if (!canManuscript) {
      toast.error('You do not have access to the manuscript');
      return;
    }
    navigate(`/editor/${draftId}`);
  };

  // ── Create new task (author only) ──
  const createTask = async (data) => {
    try {
      const res = await fetch(base, {
        method: 'POST', credentials: 'omit', headers,
        body: JSON.stringify(data),
      });
      if (res.ok) { const d = await res.json(); setTasks(d.tasks || []); return true; }
      const e = await res.json().catch(() => ({}));
      toast.error(e.error || 'Could not create task');
    } catch (e) { console.error('[TasksTab] create error', e); }
    return false;
  };

  // ── Delete task (author only) ──
  const deleteTask = async (taskId) => {
    try {
      const res = await fetch(`${base}/${taskId}`, {
        method: 'DELETE', credentials: 'omit', headers,
      });
      if (res.ok) { const d = await res.json(); setTasks(d.tasks || []); return true; }
      const e = await res.json().catch(() => ({}));
      toast.error(e.error || 'Could not delete task');
    } catch (e) { console.error('[TasksTab] delete error', e); }
    return false;
  };

  // ── Render ──
  // Author sees all tasks; collaborators only see their assigned tasks (backend also filters, this is defensive)
  const visibleTasks = isAuthor
    ? tasks
    : tasks.filter(t => (t.assignees || []).map(String).includes(String(currentUserId)));

  // Progress: author tracks overall completions; collaborator tracks their personal completions
  const completedCount = isAuthor
    ? visibleTasks.filter(t => t.status === 'completed').length
    : visibleTasks.filter(t => ((t.assignee_statuses || {})[String(currentUserId)]) === 'completed').length;

  const openTask = visibleTasks.find(t => t._id === openTaskId) || null;

  if (loading) return <div className="ct-loading">Loading tasks…</div>;

  return (
    <div className="ct-panel">
      {/* Header */}
      <div className="ct-header">
        <span className="ct-title">Cover Tasks</span>
        {visibleTasks.length > 0 && (
          <span className="ct-progress">{completedCount}/{visibleTasks.length}</span>
        )}
        {isAuthor && (
          <button className="ct-new-btn" onClick={() => setCreating(true)} title="New task">
            + New Task
          </button>
        )}
      </div>

      {/* Progress bar */}
      {visibleTasks.length > 0 && (
        <div className="ct-progress-bar">
          <div
            className="ct-progress-fill"
            style={{ width: `${(completedCount / visibleTasks.length) * 100}%` }}
          />
        </div>
      )}

      <div className="ct-list">
        {visibleTasks.length === 0 && (
          <div className="ct-empty">
            {isAuthor ? 'No tasks yet. Click "+ New Task" to add one.' : 'No tasks assigned to you yet.'}
          </div>
        )}
        {visibleTasks.map(task => (
          <TaskRow
            key={task._id}
            task={task}
            avatars={avatars}
            onClick={() => setOpenTaskId(task._id)}
          />
        ))}
      </div>

      {/* ── Detail modal ── */}
      {openTask && (
        <TaskDetailModal
          task={openTask}
          team={team}
          avatars={avatars}
          isAuthor={isAuthor}
          currentUserId={currentUserId}
          canManuscript={canManuscript}
          hasManuscript={hasManuscript}
          draftTitle={draftTitle}
          onClose={() => setOpenTaskId(null)}
          onStartTask={() => startTask(openTask._id)}
          onMarkComplete={() => markComplete(openTask._id)}
          onCancelTask={() => cancelTask(openTask._id)}
          onUnassignSelf={() => unassignSelf(openTask._id)}
          onUpdateTask={(patch) => updateTask(openTask._id, patch)}
          onAddComment={(text) => addComment(openTask._id, text)}
          onDeleteTask={async () => {
            const ok = await deleteTask(openTask._id);
            if (ok) setOpenTaskId(null);
            return ok;
          }}
          onGoToManuscript={goToManuscript}
        />
      )}

      {/* ── Create modal (author only) ── */}
      {creating && (
        <TaskCreateModal
          team={team}
          avatars={avatars}
          onClose={() => setCreating(false)}
          onCreate={async (data) => {
            const ok = await createTask(data);
            if (ok) setCreating(false);
            return ok;
          }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  MAIN SIDEBAR COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const TABS = [
  { key: 'info',        label: 'Info' },
  { key: 'chat',        label: 'Chat' },
  { key: 'annotations', label: 'Print Specifications' },
  { key: 'team',        label: 'Team' },
  { key: 'tasks',       label: 'Tasks' },
  { key: 'activity',    label: 'Activity' },
];

// Map collabSideView values to tab keys
const COLLAB_TAB_MAP = { collaborators: 'team', comments: 'chat', changes: 'activity', tasks: 'tasks' };

export default function CoverRightSidebar({
  draftId, versionId,
  isAuthor, collaboratorRole,
  versionData, onCriteriaUpdated,
  canvas, canvasReady, faceDims,
  activeObjComment, onCloseObjComment,
  objCommentCounts, onObjCountChange,
  onHighlightObject,
  collabSideView, setCollabSideView,
  onInvite,
  onRestoreCheckpoint,
  checkpointRestoring,
  triggerChange,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const prevObjComment = useRef(null);

  // Auto-switch to Annotations tab when an object comment is opened from the canvas
  useEffect(() => {
    if (activeObjComment && !prevObjComment.current) {
      setActiveTab('annotations');
      setCollapsed(false); // force-expand if sidebar is hidden
    }
    prevObjComment.current = activeObjComment;
  }, [activeObjComment]);

  // Auto-switch tab when CollaboratePanel buttons set collabSideView
  useEffect(() => {
    if (collabSideView && COLLAB_TAB_MAP[collabSideView]) {
      setActiveTab(COLLAB_TAB_MAP[collabSideView]);
      setCollapsed(false); // force-expand if sidebar is hidden
      // Reset so re-clicking the same button works as a toggle
      setCollabSideView?.(null);
    }
  }, [collabSideView, setCollabSideView]);

  return (
    <div className={`feedback-panel ${collapsed ? 'fp-collapsed' : ''}`}>
      {/* Collapse / Expand toggle */}
      <button
        className="fp-collapse-btn"
        onClick={() => setCollapsed(c => !c)}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
      </button>

      {!collapsed && (
        <>
          {/* Tab bar */}
          <div className="crs-tab-bar">
            {TABS.map(tab => (
              <button
                key={tab.key}
                className={`crs-tab${activeTab === tab.key ? ' crs-tab-active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                {tab.key === 'annotations' && Object.keys(objCommentCounts).length > 0 && (
                  <span className="crs-tab-badge">{Object.values(objCommentCounts).reduce((a, b) => a + b, 0)}</span>
                )}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {activeTab === 'info' && (
            <InfoTab
              draftId={draftId} versionId={versionId}
              isAuthor={isAuthor} versionData={versionData}
              onCriteriaUpdated={onCriteriaUpdated}
            />
          )}

          {activeTab === 'chat' && (
            <ChatTab
              draftId={draftId} versionId={versionId}
              isAuthor={isAuthor} collaboratorRole={collaboratorRole}
            />
          )}

          {activeTab === 'annotations' && (
            <AnnotationsTab
              draftId={draftId} versionId={versionId} canvas={canvas} canvasReady={canvasReady}
              faceDims={faceDims}
              triggerChange={triggerChange}
              activeObjComment={activeObjComment}
              onCloseObjComment={onCloseObjComment}
              objCommentCounts={objCommentCounts}
              onObjCountChange={onObjCountChange}
              onHighlightObject={onHighlightObject}
            />
          )}

          {activeTab === 'team' && (
            <TeamTab
              draftId={draftId} versionId={versionId}
              isAuthor={isAuthor}
              onInvite={onInvite}
            />
          )}

          {activeTab === 'tasks' && (
            <TasksTab
              draftId={draftId} versionId={versionId}
              isAuthor={isAuthor}
            />
          )}

          {activeTab === 'activity' && (
            <ActivityTab
              draftId={draftId} versionId={versionId}
              onRestoreCheckpoint={onRestoreCheckpoint}
              checkpointRestoring={checkpointRestoring}
            />
          )}
        </>
      )}
    </div>
  );
}
